import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { JoinModal } from 'features/paid-membership/JoinModal';
import { trackEvent } from 'utils/analyticsUtils';

/**
 * CTA-style pill to surface M+ (membership plus) opt-in, with copy or children and layout variants
 * for shop cart areas.
 *
 * @param {Object} props
 * @param {'desktop'|'mobileWeb'|'native'} [props.layout] - Presentation preset; default `desktop`.
 * @param {ReactNode} props.message - CTA from `usePlusMembershipOptInPill` (parent only mounts when `showPlusMembershipOptInPill`).
 * @param {string | null} [props.messageVariant] - Copy branch from `plusMembershipOptInPillMessageVariant` for Amplitude; mirrors `data-plus-membership-opt-in-pill-message-variant` on the message root.
 * @param {() => void} props.onDismiss - Called when the user dismisses the offer (X); parent should
 *   stop rendering the pill and restore surrounding layout (e.g. full-width cart button on mobile).
 * @returns {import('react').JSX.Element}
 */
function PlusMembershipOptInPill({ layout = 'desktop', message, messageVariant = null, onDismiss }) {
    const [showJoinModal, setShowJoinModal] = useState(false);

    useEffect(() => {
        trackEvent('plus membership cart pill view', {
            message: messageVariant,
        });
    }, [messageVariant]);

    const handleOptInClick = () => {
        trackEvent('plus membership cart pill click', {
            message: messageVariant,
        });
        setShowJoinModal(true);
    };

    const handleDismiss = () => {
        trackEvent('plus membership cart pill dismiss', {
            message: messageVariant,
        });
        onDismiss();
    };

    return (
        <>
            <div
                role="region"
                aria-label="Plus Membership savings offer"
                className={clsx(
                    'min-w-0 items-center justify-center rounded-full',
                    'bg-cranberry text-white if:bg-blueberry',
                    {
                        'flex h-[44px] max-h-[44px] min-h-[44px] w-full max-w-full min-w-0 shrink-0 items-center py-0 pr-5 pl-20':
                            layout === 'mobileWeb',
                        'inline-flex h-auto min-h-[44px] max-w-full self-end pr-5 pl-20': layout === 'desktop',
                        'fixed right-20 bottom-[max(24px,env(safe-area-inset-bottom,0px))] z-1200 inline-flex h-[44px] min-h-[44px] w-auto max-w-[calc(100vw-40px)] min-w-0 shrink-0 items-center overflow-hidden pr-5 pl-20':
                            layout === 'native',
                    }
                )}
            >
                <button
                    type="button"
                    onClick={handleOptInClick}
                    className={clsx(
                        'min-w-0 flex-1 text-center',
                        'font-inherit border-0 bg-transparent p-0 text-inherit',
                        {
                            'text-body-xxs line-clamp-2 leading-tight break-words': layout === 'mobileWeb',
                            'text-body-xs whitespace-normal': layout === 'desktop',
                            'text-body-xs line-clamp-1 w-full min-w-0': layout === 'native',
                        }
                    )}
                >
                    {message}
                </button>
                <button
                    type="button"
                    className="flex min-h-[32px] min-w-[32px] shrink-0 items-center justify-center self-center rounded-full border-0 bg-transparent p-0 text-white transition-opacity hover:opacity-80"
                    onClick={handleDismiss}
                    aria-label="Dismiss offer"
                >
                    <XMarkIcon className="h-15 w-15" strokeWidth={2} aria-hidden />
                </button>
            </div>
            <JoinModal onClose={() => setShowJoinModal(false)} open={showJoinModal} />
        </>
    );
}

export default PlusMembershipOptInPill;
